import { Check, ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  findSkill,
  noteSubjectValue,
  parseNoteSubject,
  startingTeacherRoute,
} from "../data/starting-teacher-route.js";

function competencyIdFor(value: string): string | null {
  const subject = parseNoteSubject(value);
  if (!subject) return null;
  if (subject.kind === "competency") return subject.id;
  return findSkill(subject.id)?.competency.id ?? null;
}

function includesQuery(title: string, query: string): boolean {
  return title.toLowerCase().includes(query);
}

export function StartingTeacherNoteSubjectField({
  id,
  value,
  onValueChange,
}: {
  id: string;
  value: string;
  onValueChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(competencyIdFor(value));
  const selected = parseNoteSubject(value);
  const selectedLabel = selected
    ? t(selected.kind === "competency" ? "startingTeacher.noteSubjectCompetency" : "startingTeacher.noteSubjectSkill", {
        title: t(
          selected.kind === "competency"
            ? `startingTeacher.competencies.${selected.id}.title`
            : `startingTeacher.skills.${selected.id}.title`,
        ),
      })
    : "";
  const normalized = query.trim().toLowerCase();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  function openList() {
    setQuery("");
    setExpandedId(competencyIdFor(value));
    setOpen(true);
  }

  function choose(next: string) {
    onValueChange(next);
    setQuery("");
    setOpen(false);
  }

  const groups = startingTeacherRoute.flatMap((competency) => {
    const title = t(`startingTeacher.competencies.${competency.id}.title`);
    const skills = competency.skills.map((skill) => ({
      id: skill.id,
      title: t(`startingTeacher.skills.${skill.id}.title`),
    }));
    const competencyMatches = includesQuery(title, normalized);
    const matchingSkills = skills.filter((skill) => includesQuery(skill.title, normalized));
    if (normalized && !competencyMatches && matchingSkills.length === 0) return [];
    return [
      {
        id: competency.id,
        title,
        skills: normalized && !competencyMatches ? matchingSkills : skills,
        expanded: normalized ? true : expandedId === competency.id,
      },
    ];
  });

  return (
    <div className="relative" ref={root}>
      <input
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        className="h-11 w-full rounded-md border border-input bg-background pr-9 pl-3 text-sm text-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        data-control-id="starting-teacher.note.subject"
        id={id}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onClick={() => {
          if (!open) openList();
        }}
        onFocus={() => {
          if (!open) openList();
        }}
        placeholder={t("startingTeacher.noteSubjectSearch")}
        role="combobox"
        value={open ? query : selectedLabel}
      />
      <ChevronDown
        aria-hidden="true"
        className={`pointer-events-none absolute top-3.5 right-3 size-4 text-muted-foreground ${open ? "rotate-180" : ""}`}
      />
      {open ? (
        <div
          className="mt-1 max-h-64 overflow-y-auto rounded-md border border-input bg-popover p-1 text-popover-foreground shadow-md"
          id={listId}
          role="listbox"
        >
          {groups.length === 0 ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">{t("startingTeacher.noteSubjectEmpty")}</p>
          ) : (
            groups.map((group) => (
              <div aria-label={group.title} key={group.id} role="group">
                <div className="flex items-center">
                  <button
                    aria-expanded={group.expanded}
                    aria-label={t("startingTeacher.noteSubjectExpand", { title: group.title })}
                    className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                    data-control-id={`starting-teacher.note.subject.expand.${group.id}`}
                    onClick={() => {
                      if (normalized) return;
                      setExpandedId((current) => (current === group.id ? null : group.id));
                    }}
                    type="button"
                  >
                    <ChevronRight
                      aria-hidden="true"
                      className={`size-4 transition-transform ${group.expanded ? "rotate-90" : ""}`}
                    />
                  </button>
                  <button
                    aria-selected={selected?.kind === "competency" && selected.id === group.id}
                    className="flex min-h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                    data-control-id={`starting-teacher.note.subject.option.competency.${group.id}`}
                    onClick={() => choose(noteSubjectValue({ kind: "competency", id: group.id }))}
                    role="option"
                    type="button"
                  >
                    <span className="min-w-0 flex-1">{group.title}</span>
                    <Check
                      aria-hidden="true"
                      className={`size-4 shrink-0 text-primary ${
                        selected?.kind === "competency" && selected.id === group.id ? "" : "invisible"
                      }`}
                    />
                  </button>
                </div>
                {group.expanded
                  ? group.skills.map((skill) => (
                      <button
                        aria-selected={selected?.kind === "skill" && selected.id === skill.id}
                        className="flex min-h-8 w-full items-center gap-2 rounded-md pr-2 pl-10 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                        data-control-id={`starting-teacher.note.subject.option.skill.${skill.id}`}
                        key={skill.id}
                        onClick={() => choose(noteSubjectValue({ kind: "skill", id: skill.id }))}
                        role="option"
                        type="button"
                      >
                        <span className="min-w-0 flex-1">{skill.title}</span>
                        <Check
                          aria-hidden="true"
                          className={`size-4 shrink-0 text-primary ${
                            selected?.kind === "skill" && selected.id === skill.id ? "" : "invisible"
                          }`}
                        />
                      </button>
                    ))
                  : null}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
